import { Grid } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { makeStyles } from "@mui/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import { BarLoader } from "react-spinners";
import type { Datastore, DatastoreFolder } from "../../../types/datastore";
import type {
	ImportBreadcrumbs,
	ManagementError,
	ManagementResult,
	ManagementViewProps,
} from "../../../types/management-ui";
import DialogSelectFolder from "../../components/dialogs/select-folder";
import GridContainerErrors from "../../components/grid-container-errors";
import TextFieldPassword from "../../components/text-field/password";
import TextFieldPath from "../../components/text-field/path";
import importService from "../../services/import";
import SelectFieldFileDestination from "../../components/select-field/file-destination";
import type { FileDestination } from "../../../types/components";

// The form's empty binary value is passed through until FileReader finishes.
// The shared import service signature must also accept this existing request value.
type ImportDatastoreRequest = (
	type: string,
	data: string,
	binary: ArrayBuffer | "",
	password: string,
	breadcrumbs: ImportBreadcrumbs,
	archive?: Blob,
	destination?: FileDestination | null,
) => ReturnType<typeof importService.importDatastore>;

const useStyles = makeStyles((theme) => ({
	textField: {
		width: "100%",
		[theme.breakpoints.up("md")]: {
			width: "440px",
		},
	},
}));

const OtherImportView = (_props: ManagementViewProps) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const [exportFormat, setExportFormat] = React.useState("psono_pw_json");
	const [encoding, setEncoding] = React.useState("utf-8");
	const [fileContent, setFileContent] = React.useState("");
	const [fileBinary, setFileBinary] = React.useState<ArrayBuffer | "">("");
	const [fileName, setFileName] = React.useState("");
	const [archive, setArchive] = React.useState<File>();
	const [destination, setDestination] = React.useState<FileDestination | null>(
		null,
	);
	const [percentageComplete, setPercentageComplete] = React.useState(0);
	const [processing, setProcessing] = React.useState(false);
	const [errors, setErrors] = React.useState<string[]>([]);
	const [messages, setMessages] = React.useState<string[]>([]);
	const [password, setPassword] = React.useState("");
	const [selectFolderOpen, setSelectFolderOpen] = React.useState(false);
	const [breadcrumbs, setBreadcrumbs] = React.useState<ImportBreadcrumbs>({
		id_breadcrumbs: [],
		path: [],
	});

	let openRequests = 0;
	let closedRequest = 0;

	React.useEffect(() => {
		importService.on("import-started", () => {
			setProcessing(true);
		});

		importService.on("create-secret-started", () => {
			openRequests = openRequests + 1;
			setPercentageComplete(
				Math.round((closedRequest / openRequests) * 1000) / 10,
			);
		});

		importService.on("create-secret-complete", () => {
			closedRequest = closedRequest + 1;
			setPercentageComplete(
				Math.round((closedRequest / openRequests) * 1000) / 10,
			);
		});

		importService.on("import-complete", () => {
			openRequests = 0;
			closedRequest = 0;
			setProcessing(false);
		});
		importService.on("attachment-progress", (percentage) =>
			setPercentageComplete(percentage as number),
		);
	}, []);

	const importPasswords = () => {
		setMessages([]);
		setErrors([]);

		const onSuccess = (data: ManagementResult) => {
			setMessages(data.msgs);
			setErrors([]);
		};
		const onError = (data: ManagementError | Error | undefined) => {
			console.log(data);
			setMessages([]);
			const errors = data && "errors" in data ? data.errors : undefined;
			if (errors?.length) {
				if (errors[0] === "RESOURCE_NOT_FOUND") {
					setErrors(["FEATURE_NOT_SUPPORTED_SERVER_REQUIRES_UPGRADE"]);
				} else {
					setErrors(errors);
				}
			} else {
				setErrors([data instanceof Error ? data.message : "UNKNOWN_ERROR"]);
			}
			openRequests = 0;
			closedRequest = 0;
			setProcessing(false);
		};

		(importService.importDatastore as ImportDatastoreRequest)(
			exportFormat,
			fileContent,
			fileBinary,
			password,
			breadcrumbs,
			archive,
			destination,
		).then(onSuccess, onError);
	};

	const setSelectedPath = (path: DatastoreFolder[]) => {
		setBreadcrumbs({
			id_breadcrumbs: path.map((node) => node.id),
			path: Array.from(path),
		});
	};

	const onSelectNodeForImport = (
		newBreadcrumbs: Partial<ImportBreadcrumbs>,
	) => {
		setSelectedPath(newBreadcrumbs.path || []);
		setSelectFolderOpen(false);
	};

	const isSelectableForImport = (node: Datastore) => {
		if (
			Object.hasOwn(node, "share_id") &&
			!Object.hasOwn(node, "share_rights")
		) {
			return false;
		}
		if (!Object.hasOwn(node, "share_rights")) {
			return true;
		}
		return !!(node.share_rights!.read && node.share_rights!.write);
	};

	const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault();

		const file = event.target.files?.[0];
		if (!file) return;
		setFileName(file.name);
		setArchive(file);
		if (exportFormat === "bitwarden_zip") {
			setFileContent("");
			setFileBinary("");
			return;
		}
		const reader = new FileReader();
		reader.onload = (e) => {
			// readAsArrayBuffer supplies an ArrayBuffer on successful load.
			const arrayBuffer = e.target!.result as ArrayBuffer;
			const decoder = new TextDecoder(encoding);
			const textContent = decoder.decode(new Uint8Array(arrayBuffer));

			setFileContent(textContent);
			setFileBinary(arrayBuffer);
		};
		reader.readAsArrayBuffer(event.target.files![0]);
	};

	const getHelpText = importService.getImporterHelp(exportFormat);

	return (
		<Grid container>
			<Grid item xs={12} sm={12} md={12}>
				<h2>{t("IMPORT")}</h2>
				<p>{t("IMPORT_DESCRIPTION")}</p>
				<Divider style={{ marginBottom: "20px" }} />
			</Grid>
			<Grid item xs={12} sm={12} md={12}>
				<FormControl
					variant="outlined"
					margin="dense"
					size="small"
					className={classes.textField}
					required
				>
					<InputLabel id="export-type-select-label-label">
						{t("TYPE")}
					</InputLabel>
					<Select
						labelId="export-type-select-label-label"
						id="export-type-select-label"
						value={exportFormat}
						disabled={processing}
						onChange={(event) => {
							setExportFormat(event.target.value);
							setFileName("");
							setArchive(undefined);
							setFileContent("");
							setFileBinary("");
							setDestination(null);
						}}
						label={t("TYPE")}
					>
						{importService.getImporter().map((importer, index) => {
							return (
								<MenuItem key={index} value={importer["value"]}>
									{importer["name"]}
								</MenuItem>
							);
						})}
					</Select>
				</FormControl>
			</Grid>
			{exportFormat === "psono_pw_json" && (
				<Grid item xs={12} sm={12} md={12}>
					<TextFieldPassword
						className={classes.textField}
						variant="outlined"
						margin="dense"
						size="small"
						id="password"
						label={t("PASSWORD")}
						helperText={t("ENTER_PASSWORD_IF_YOUR_EXPORT_IS_ENCRYPTED")}
						name="password"
						autoComplete="off"
						value={password}
						onChange={(
							event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
						) => {
							setPassword(event.target.value);
						}}
					/>
				</Grid>
			)}
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				style={{ marginBottom: "8px", marginTop: "8px" }}
			>
				<Button variant="outlined" disabled={processing} component="label">
					{fileName ? fileName : t("SELECT_EXPORT_FILE")}
					<input type="file" hidden onChange={onFileChange} />
				</Button>
			</Grid>
			{exportFormat === "bitwarden_zip" && (
				<Grid item xs={12}>
					<SelectFieldFileDestination
						className={classes.textField}
						variant="outlined"
						margin="dense"
						size="small"
						value={destination}
						onChange={setDestination}
						required
						emptyMessage="BITWARDEN_ZIP_REQUIRES_FILE_STORAGE"
						disabled={processing}
					/>
				</Grid>
			)}
			{exportFormat !== "bitwarden_zip" && (
				<Grid item xs={12} sm={12} md={12}>
					<FormControl
						variant="outlined"
						margin="dense"
						size="small"
						className={classes.textField}
						required
					>
						<InputLabel id="export-encoding-select-label-label">
							{t("ENCODING")}
						</InputLabel>
						<Select
							labelId="export-encoding-select-label-label"
							id="export-encoding-select-label"
							value={encoding}
							onChange={(event) => {
								setEncoding(event.target.value);
							}}
							label={t("ENCODING")}
						>
							{[
								"utf-8",
								"utf-16",
								"iso-8859-2",
								"iso-8859-3",
								"iso-8859-4",
								"iso-8859-5",
								"iso-8859-6",
								"iso-8859-7",
								"iso-8859-8",
								"iso-8859-8-i",
								"iso-8859-10",
								"iso-8859-13",
								"iso-8859-14",
								"iso-8859-15",
								"iso-8859-16",
								"windows-874",
								"windows-1250",
								"windows-1251",
								"windows-1252",
								"windows-1254",
								"windows-1255",
								"windows-1256",
								"windows-1257",
								"windows-1258",
							].map((encoding, index) => (
								<MenuItem key={index} value={encoding}>
									{encoding}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			)}
			<Grid item xs={12} sm={12} md={12}>
				<TextFieldPath
					className={classes.textField}
					variant="outlined"
					margin="dense"
					size="small"
					required
					value={breadcrumbs.path}
					setPath={setSelectedPath}
					onOpenFolderSelect={() => setSelectFolderOpen(true)}
					helperText="Choose where the Import folder will be created."
				/>
			</Grid>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				style={{ marginBottom: "8px", marginTop: "8px" }}
			>
				<Button
					variant="contained"
					color="primary"
					onClick={importPasswords}
					disabled={
						processing ||
						!fileName ||
						(exportFormat === "bitwarden_zip" && !destination)
					}
				>
					<span style={!processing ? {} : { display: "none" }}>
						{t("IMPORT")}
					</span>
					<BarLoader
						color={"#FFF"}
						height={17}
						width={37}
						loading={processing}
					/>
				</Button>
			</Grid>
			{processing && (
				<Grid
					item
					xs={12}
					sm={12}
					md={12}
					style={{ marginBottom: "8px", marginTop: "8px" }}
				>
					<Box display="flex" alignItems="center">
						<Box width="100%" mr={1}>
							<LinearProgress
								variant="determinate"
								value={percentageComplete}
							/>
						</Box>
						<Box minWidth={35}>
							<span style={{ color: "white", whiteSpace: "nowrap" }}>
								{percentageComplete} %
							</span>
						</Box>
					</Box>
				</Grid>
			)}
			{getHelpText && (
				<MuiAlert
					severity="info"
					style={{
						marginBottom: "5px",
						marginTop: "5px",
					}}
				>
					{getHelpText()}
				</MuiAlert>
			)}
			<GridContainerErrors errors={errors} setErrors={setErrors} />
			{messages && (
				<Grid item xs={12} sm={12} md={12}>
					{messages.map((prop, index) => {
						return (
							<MuiAlert
								key={index}
								severity="info"
								style={{
									marginBottom: "5px",
									marginTop: "5px",
								}}
							>
								{t(prop)}
							</MuiAlert>
						);
					})}
				</Grid>
			)}
			{selectFolderOpen && (
				<DialogSelectFolder
					open={selectFolderOpen}
					onClose={() => setSelectFolderOpen(false)}
					title={`${t("SELECT")} ${t("FOLDER")}`}
					onSelectNode={onSelectNodeForImport}
					isSelectable={isSelectableForImport}
				/>
			)}
		</Grid>
	);
};

export default OtherImportView;
